import { Trans } from 'react-i18next';
import { useCallback } from 'react';

import { EducationalMaterial } from '@dreryk/egabinet-knowledge-base-frontend-api';
import { VisitPatient } from '@dreryk/egabinet-visit-frontend-api';

import {
   ActionBar,
   ButtonVariant,
   dispatchSnackbarPositiveEvent,
   EmptyStatePlaceholder,
   SelectableTable,
} from '@dreryk/react-components';
import { useFormController } from '@dreryk/react-common';

import { VisitId } from '~libs.shared.visit';

import { educationalMaterialsTranslationKeys as t } from '../translations';
import { EducationalMaterialShareShowModalInterceptor } from '../share/EducationalMaterialShareShowModalInterceptor';

import { EducationalMaterialFormBean, EducationalMaterialRemoveHandler } from './EducationalMaterialsTable.types';
import {
   useEducationalMaterialsColumns,
   useEducationalMaterialsDataConfiguration,
   useEducationalMaterialVisitOperationRegisterCommand,
} from './EducationalMaterialsTable.hooks';

const TABLE_PAGE_SIZE = 20;

type EducationalMaterialsTableProps = Readonly<{
   searchText: string | null;
   patientEmail?: VisitPatient['emailAddress'];
   visitId: VisitId;
}>;

export const EducationalMaterialsTable = (props: EducationalMaterialsTableProps): JSX.Element => {
   const { register, watch, reset, sync } = useFormController<EducationalMaterialFormBean>({
      educationalMaterials: [],
   });

   const dataConfiguration = useEducationalMaterialsDataConfiguration({
      searchText: props.searchText,
   });
   const columns = useEducationalMaterialsColumns({ patientEmail: props.patientEmail, visitId: props.visitId });

   const educationalMaterials = watch('educationalMaterials') ?? [];
   const educationalMaterialsCount = educationalMaterials?.length ?? 0;

   const { executeAsync: registerEducationalMaterialOperation } = useEducationalMaterialVisitOperationRegisterCommand(
      educationalMaterials
   );
   const handleRemoveEducationalMaterial: EducationalMaterialRemoveHandler = (educationalMaterialId) => {
      sync({ educationalMaterials: educationalMaterials.filter(({ id }) => id !== educationalMaterialId) });
      return dispatchSnackbarPositiveEvent(t.removedMaterialFromMaterialShippingList);
   };
   const handleEducationalMaterialsShareSuccess = useCallback(async () => {
      await registerEducationalMaterialOperation('SENT').finally(reset);
   }, [registerEducationalMaterialOperation, reset]);

   return (
      <>
         <SelectableTable<EducationalMaterial>
            testId={'educationalMaterials'}
            dataConfiguration={dataConfiguration}
            keyExtractor={'id'}
            columns={columns}
            pageSize={TABLE_PAGE_SIZE}
            placeholder={<EmptyStatePlaceholder heading={t.form.table.placeholder} testId={'noEducationalMaterials'} />}
            controller={register('educationalMaterials')}
         />
         <EducationalMaterialShareShowModalInterceptor
            visitId={props.visitId}
            patientEmail={props.patientEmail}
            educationalMaterials={educationalMaterials}
            onEducationalMaterialRemoved={handleRemoveEducationalMaterial}
            onEducationalMaterialsShareSuccess={handleEducationalMaterialsShareSuccess}
         >
            <ActionBar.PrimaryButton
               label={<Trans i18nKey={t.shareSelected} values={{ selectedCount: educationalMaterialsCount }} />}
               variant={ButtonVariant.PRIMARY}
               testId='submit'
               disabled={!educationalMaterialsCount}
            />
         </EducationalMaterialShareShowModalInterceptor>
      </>
   );
};
